'use client';
import { useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';

import { useAuth, useEventBookmarkAction, useT } from '@/hooks';
import { EventMarket, EventOutcome, EventResponse, Market, Outcome } from '@/types';
import { createMarketFromGame, getOutcomes, parseLiveScore } from '@/utils';

import { TRADE_SIDES } from '@/constants';

interface TradeModalState {
  isOpen: boolean;
  market: Market | EventMarket | null;
  outcome: Outcome | EventOutcome | null;
  isBuyOrder: boolean;
  teamColor?: string;
  event?: EventResponse;
  winnerName?: string;
  isResolved?: boolean;
}

const INITIAL_TRADE_MODAL_STATE: TradeModalState = {
  isOpen: false,
  market: null,
  outcome: null,
  isBuyOrder: true,
  teamColor: undefined,
};

interface TradingProps {
  selectedEvent: EventResponse | null;
  effectiveSportSlug: string;
  bookmarkedEventIds: string[];
  apiEvents: EventResponse[];
}

/**
 * Custom hook for managing sports trading state, modal interactions, and bookmarking.
 */
export const useSportCategoryTrading = ({
  selectedEvent,
  effectiveSportSlug,
  bookmarkedEventIds,
  apiEvents,
}: TradingProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { checkIsBookmarked, handleBookmarkToggle } = useEventBookmarkAction();
  const { t } = useT();

  const [selectedSide, setSelectedSide] = useState<'yes' | 'no'>(TRADE_SIDES.YES);
  const [sidebarTab, setSidebarTab] = useState<'buy' | 'sell'>('buy');
  const [tradeModalState, setTradeModalState] =
    useState<TradeModalState>(INITIAL_TRADE_MODAL_STATE);
  const [activeMarketData, setActiveMarketData] = useState<{
    market: Market;
    outcome: Outcome;
    teamColor?: string;
    event?: EventResponse;
    winnerName?: string;
    isResolved?: boolean;
  } | null>(null);

  const resetSelections = () => {
    setActiveMarketData(null);
  };

  const handleOpenTradeModal = (
    market: Market | EventMarket,
    outcome: Outcome | EventOutcome,
    isBuy: boolean,
    isYes: boolean = true,
    teamColor?: string,
    event?: EventResponse,
    winnerName?: string,
    isResolved?: boolean
  ) => {
    if (isDesktop) {
      setSelectedSide(isYes ? 'yes' : 'no');
      setSidebarTab(isBuy ? 'buy' : 'sell');
      return;
    }

    setTradeModalState({
      isOpen: true,
      market,
      outcome,
      isBuyOrder: isBuy,
      teamColor,
      event,
      winnerName,
      isResolved,
    });
  };

  const handleCloseTradeModal = () => {
    setTradeModalState(INITIAL_TRADE_MODAL_STATE);
  };

  const getEventResolution = (event: EventResponse, isPreviousMarket?: boolean) => {
    let winnerName = undefined;
    const isResolved = !!(
      isPreviousMarket ||
      event.ended ||
      event.status?.toLowerCase() === 'final' ||
      event.status?.toLowerCase() === 'ended'
    );

    if (isResolved) {
      const parsedScore = parseLiveScore(event.score);
      const homeScore = parseInt(parsedScore?.home || event.homeTeam?.score || '0');
      const awayScore = parseInt(parsedScore?.away || event.awayTeam?.score || '0');
      if (homeScore > awayScore) winnerName = event.homeTeam?.name;
      else if (awayScore > homeScore) winnerName = event.awayTeam?.name;
      else winnerName = t('sports.draw', { defaultValue: 'Draw' });
    }
    return { isResolved, winnerName };
  };

  const handleTeamClick = (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => {
    const data = createMarketFromGame(event, teamIndex, effectiveSportSlug, isPreviousMarket);
    const { isResolved, winnerName } = getEventResolution(event, isPreviousMarket);

    const marketData = {
      ...data,
      event,
      winnerName,
      isResolved,
    };

    setActiveMarketData(marketData);
    handleOpenTradeModal(
      marketData.market,
      marketData.outcome,
      true, // isBuy
      true, // isYes — clicked team is always at outcomes[0] (YES) in the virtual market,
      // so always select 'yes' side to match the reordered clobTokenIds[0]
      marketData.teamColor,
      event,
      winnerName,
      isResolved
    );
  };

  const handlePropTradeClick = (marketId: string, outcomeId: string, isYes: boolean = true) => {
    let parentEvent: EventResponse | undefined;
    let foundMarket: EventMarket | undefined;

    for (const event of apiEvents) {
      const market = event.markets.find((m) => String(m?.id) === String(marketId));
      if (market) {
        foundMarket = market;
        parentEvent = event;
        break;
      }
    }

    if (!foundMarket) return;

    const outcomes = getOutcomes(foundMarket);
    const outcomeIdx = outcomes.findIndex((o) => String(o?.id) === String(outcomeId));
    const foundOutcome = outcomeIdx !== -1 ? outcomes[outcomeIdx] : outcomes[0];
    if (!foundOutcome) return;

    // Determine side based on index (index 1 is 'no', else 'yes')
    const isYesOutcome = outcomeIdx === 1 ? false : true;

    setActiveMarketData({
      market: foundMarket as any,
      outcome: foundOutcome as any,
      event: parentEvent,
      isResolved: false,
    });

    handleOpenTradeModal(
      foundMarket,
      foundOutcome,
      true, // isBuy (always true when starting from listing)
      isYesOutcome,
      undefined,
      parentEvent,
      undefined,
      false
    );
  };

  const handleBookmarkClick = (e?: React.MouseEvent) => {
    if (!selectedEvent) return;
    handleBookmarkToggle(e, selectedEvent, 'sport');
  };

  const isBookmarked = selectedEvent ? checkIsBookmarked(selectedEvent.id) : false;

  return {
    selectedSide,
    tradeModalState,
    activeMarketData,
    isBookmarked,
    isDesktop,
    sidebarTab,
    setSelectedSide,
    setSidebarTab,
    resetSelections,
    handleOpenTradeModal,
    handleCloseTradeModal,
    handleTeamClick,
    handlePropTradeClick,
    handleBookmarkClick,
    getEventResolution,
  };
};
